import { useState } from 'react';
import { useI18n } from '../../contexts/I18nContext';
import type { Actor, ActorInput } from '../../lib/actorsApi';
import { CompanyLogoField } from '../settings/CompanyLogoField';

/** Create / edit an actor: first and last name, contact, photo, notes. */
export function ActorFormModal({
  initial,
  submitting,
  onClose,
  onSubmit,
}: {
  initial?: Actor;
  submitting?: boolean;
  onClose: () => void;
  onSubmit: (input: ActorInput) => void | Promise<void>;
}) {
  const { t } = useI18n();
  const [firstName, setFirstName] = useState(initial?.firstName ?? '');
  const [lastName, setLastName] = useState(initial?.lastName ?? '');
  const [phone, setPhone] = useState(initial?.phone ?? '');
  const [email, setEmail] = useState(initial?.email ?? '');
  const [notes, setNotes] = useState(initial?.notes ?? '');
  const [avatarUrl, setAvatarUrl] = useState(initial?.avatarUrl ?? '');
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!firstName.trim() || !lastName.trim()) {
      setError(t('actors.form.required'));
      return;
    }
    try {
      await onSubmit({
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        phone: phone.trim(),
        email: email.trim(),
        notes: notes.trim(),
        avatarUrl: avatarUrl || null,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'A apărut o eroare.');
    }
  }

  const input =
    'w-full rounded border border-gray-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500';

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-lg bg-white shadow-xl">
        <form onSubmit={handleSubmit}>
          <div className="flex items-center justify-between border-b border-gray-200 px-5 py-3">
            <h2 className="text-lg font-semibold text-gray-900">
              {initial ? t('actors.form.editTitle') : t('actors.form.createTitle')}
            </h2>
            <button
              type="button"
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
              aria-label="Închide"
            >
              ✕
            </button>
          </div>
          <div className="grid grid-cols-1 gap-3 px-5 py-4 sm:grid-cols-2">
            <label className="block">
              <span className="mb-1 block text-xs font-medium text-gray-600">
                {t('actors.field.firstName')} *
              </span>
              <input
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                className={input}
                autoFocus
              />
            </label>
            <label className="block">
              <span className="mb-1 block text-xs font-medium text-gray-600">
                {t('actors.field.lastName')} *
              </span>
              <input
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                className={input}
              />
            </label>
            <label className="block">
              <span className="mb-1 block text-xs font-medium text-gray-600">
                {t('actors.field.phone')}
              </span>
              <input value={phone} onChange={(e) => setPhone(e.target.value)} className={input} />
            </label>
            <label className="block">
              <span className="mb-1 block text-xs font-medium text-gray-600">
                {t('actors.field.email')}
              </span>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className={input}
              />
            </label>
            <div className="sm:col-span-2">
              <span className="mb-1 block text-xs font-medium text-gray-600">
                {t('actors.field.photo')}
              </span>
              <CompanyLogoField kind="avatar" value={avatarUrl} onChange={setAvatarUrl} />
            </div>
            <label className="block sm:col-span-2">
              <span className="mb-1 block text-xs font-medium text-gray-600">
                {t('actors.field.notes')}
              </span>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={3}
                className={input}
              />
            </label>
          </div>
          {error && <p className="px-5 pb-2 text-sm text-red-600">{error}</p>}
          <div className="flex justify-end gap-2 border-t border-gray-200 px-5 py-3">
            <button
              type="button"
              onClick={onClose}
              className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              {t('actors.link.cancel')}
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="rounded-md bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50"
            >
              {t('common.save')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
